<script setup>
import { computed, nextTick, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { debounce } from '@chatwoot/utils';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ContactAPI from 'dashboard/api/contacts';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import { useCrmStore } from '../store/useCrmStore';
import { extractApiErrorMessage } from '../utils/error';
import { fromCurrencyCents, toCurrencyCents } from '../utils/currency';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  mode: {
    type: String,
    default: 'create',
  },
  order: {
    type: Object,
    default: () => ({}),
  },
  columns: {
    type: Array,
    default: () => [],
  },
  pipeline: {
    type: String,
    default: 'service_orders',
  },
  accountId: {
    type: [String, Number],
    required: true,
  },
  currency: {
    type: String,
    default: 'USD',
  },
});

const emit = defineEmits(['close', 'success']);

const CONTACT_SEARCH_MIN_LENGTH = 2;

const { t } = useI18n();
const crmStore = useCrmStore();
const dialogRef = ref(null);

const translationScope = computed(() => {
  if (props.pipeline === 'support') {
    return 'CRM.PIPELINES.SUPPORT';
  }

  return 'CRM.PIPELINES.SERVICE_ORDERS';
});

const isSupportPipeline = computed(() => props.pipeline === 'support');

const form = reactive({
  title: '',
  description: '',
  contact_id: null,
  conversation_id: '',
  kanban_column_id: null,
  emotion_label: 'unknown',
  emotion_confidence: '',
  emotion_reason: '',
  satisfaction_score: '',
  manual_override: false,
  value: '',
  difficulty_level: '',
  priority_level: '',
  due_date: '',
  estimated_hours: '',
  actual_hours: '',
});

const contactsSearch = ref('');
const contacts = ref([]);
const isSearchingContacts = ref(false);
const isSubmitting = ref(false);
const isOptionalSectionOpen = ref(false);

const rules = computed(() => ({
  form: {
    title: { required },
    kanban_column_id: { required },
  },
}));

const v$ = useVuelidate(rules, { form });

const columnOptions = computed(() => {
  return props.columns.map(col => ({
    label: col.name,
    value: col.id,
  }));
});

const emotionOptions = computed(() => [
  {
    value: 'very_negative',
    label: t('CRM.MODAL.FORM.EMOTION_OPTIONS.VERY_NEGATIVE'),
  },
  {
    value: 'negative',
    label: t('CRM.MODAL.FORM.EMOTION_OPTIONS.NEGATIVE'),
  },
  {
    value: 'neutral',
    label: t('CRM.MODAL.FORM.EMOTION_OPTIONS.NEUTRAL'),
  },
  {
    value: 'positive',
    label: t('CRM.MODAL.FORM.EMOTION_OPTIONS.POSITIVE'),
  },
  {
    value: 'very_positive',
    label: t('CRM.MODAL.FORM.EMOTION_OPTIONS.VERY_POSITIVE'),
  },
  {
    value: 'unknown',
    label: t('CRM.MODAL.FORM.EMOTION_OPTIONS.UNKNOWN'),
  },
]);

const priorityOptions = computed(() => [
  { value: 'low', label: t('CRM.MODAL.FORM.PRIORITY_OPTIONS.LOW') },
  { value: 'medium', label: t('CRM.MODAL.FORM.PRIORITY_OPTIONS.MEDIUM') },
  { value: 'high', label: t('CRM.MODAL.FORM.PRIORITY_OPTIONS.HIGH') },
  { value: 'urgent', label: t('CRM.MODAL.FORM.PRIORITY_OPTIONS.URGENT') },
]);

const selectedColumnLabel = computed(() => {
  const column = props.columns.find(col => col.id === form.kanban_column_id);
  return column?.name || t('CRM.MODAL.FORM.COLUMN_PLACEHOLDER');
});

const selectedContact = computed(() => {
  return contacts.value.find(contact => contact.id === form.contact_id) || null;
});

const selectedContactLabel = computed(() => {
  if (!selectedContact.value) {
    return t('CRM.MODAL.FORM.CONTACT_PLACEHOLDER');
  }

  return selectedContact.value.name || `#${selectedContact.value.id}`;
});

const selectedContactAvatar = computed(
  () => selectedContact.value?.thumbnail || null
);

const contactOptions = computed(() => {
  return contacts.value.map(contact => ({
    value: contact.id,
    label:
      contact.name || t('CRM.MODAL.FORM.CONTACT_ID_PREFIX', { id: contact.id }),
  }));
});

const hasTitleError = computed(() => v$.value.form.title.$error);
const hasColumnError = computed(() => v$.value.form.kanban_column_id.$error);

const parseIntegerOrNull = value => {
  if (value === '' || value === null || value === undefined) {
    return null;
  }

  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

const parseFloatOrNull = value => {
  if (value === '' || value === null || value === undefined) {
    return null;
  }

  const parsed = Number.parseFloat(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const resetForm = () => {
  form.title = '';
  form.description = '';
  form.contact_id = null;
  form.conversation_id = '';
  form.kanban_column_id = props.columns[0]?.id || null;

  form.emotion_label = 'unknown';
  form.emotion_confidence = '';
  form.emotion_reason = '';
  form.satisfaction_score = '';
  form.manual_override = false;

  form.value = '';
  form.difficulty_level = '';
  form.priority_level = '';
  form.due_date = '';
  form.estimated_hours = '';
  form.actual_hours = '';

  contactsSearch.value = '';
  contacts.value = [];
  isOptionalSectionOpen.value = false;
  v$.value.$reset();
};

const hydrateFormForEdit = () => {
  form.title = props.order.title || '';
  form.description = props.order.description || '';
  form.contact_id = props.order.contact_id || null;
  form.conversation_id = props.order.conversation_id || '';
  form.kanban_column_id = props.order.kanban_column_id || props.columns[0]?.id;

  const crmData = props.order.crm_data || {};

  form.emotion_label = crmData.emotion_label || 'unknown';
  form.emotion_confidence = crmData.emotion_confidence ?? '';
  form.emotion_reason = crmData.emotion_reason || '';
  form.satisfaction_score = crmData.satisfaction_score ?? '';
  form.manual_override = Boolean(crmData.manual_override);

  form.value = fromCurrencyCents(crmData.value_cents);
  form.difficulty_level = crmData.difficulty_level ?? '';
  form.priority_level = crmData.priority_level || '';
  form.due_date = crmData.due_date || '';
  form.estimated_hours = crmData.estimated_hours ?? '';
  form.actual_hours = crmData.actual_hours ?? '';

  isOptionalSectionOpen.value = true;
};

const fetchContact = async id => {
  try {
    const response = await ContactAPI.show(id);
    const contact = response.data.payload;

    if (!contacts.value.find(current => current.id === contact.id)) {
      contacts.value = [...contacts.value, contact];
    }

    contactsSearch.value = contact.name;
  } catch (_) {
    form.contact_id = null;
    contactsSearch.value = '';
  }
};

const searchContacts = async query => {
  isSearchingContacts.value = true;
  try {
    const response = await ContactAPI.search(query);
    contacts.value = response.data.payload || [];
  } catch (_) {
    contacts.value = [];
  } finally {
    isSearchingContacts.value = false;
  }
};

const debouncedSearchContacts = debounce(searchContacts, 350);

const onContactSearch = query => {
  const normalizedQuery = query.trim();
  contactsSearch.value = normalizedQuery;

  if (normalizedQuery.length < CONTACT_SEARCH_MIN_LENGTH) {
    contacts.value = [];
    return;
  }

  debouncedSearchContacts(normalizedQuery);
};

const onSelectContact = contactId => {
  form.contact_id = contactId || null;
};

const clearSelectedContact = () => {
  form.contact_id = null;
  contactsSearch.value = '';
};

const buildSupportCrmDataPayload = () => {
  const payload = {
    emotion_label: form.emotion_label || 'unknown',
    emotion_confidence: parseFloatOrNull(form.emotion_confidence),
    emotion_reason: form.emotion_reason?.trim() || null,
    satisfaction_score: parseIntegerOrNull(form.satisfaction_score),
    manual_override: form.manual_override,
  };

  if (form.manual_override) {
    payload.emotion_source = payload.emotion_label ? 'manual' : null;
    payload.satisfaction_source = payload.satisfaction_score ? 'manual' : null;
  }

  return payload;
};

const buildServiceOrderCrmDataPayload = () => ({
  value_cents: toCurrencyCents(form.value),
  difficulty_level: parseIntegerOrNull(form.difficulty_level),
  priority_level: form.priority_level || null,
  due_date: form.due_date || null,
  estimated_hours: parseFloatOrNull(form.estimated_hours),
  actual_hours: parseFloatOrNull(form.actual_hours),
});

const onSubmit = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  const payload = {
    title: form.title,
    description: form.description,
    contact_id: form.contact_id,
    conversation_id: parseIntegerOrNull(form.conversation_id),
    kanban_column_id: form.kanban_column_id,
    crm_data: isSupportPipeline.value
      ? buildSupportCrmDataPayload()
      : buildServiceOrderCrmDataPayload(),
  };

  isSubmitting.value = true;
  try {
    if (props.mode === 'create') {
      await crmStore.createServiceOrder({
        accountId: props.accountId,
        orderData: payload,
        pipeline: props.pipeline,
      });
      useAlert(t(`${translationScope.value}.MODAL.SUCCESS_CREATE`));
    } else {
      await crmStore.updateServiceOrder({
        accountId: props.accountId,
        orderId: props.order.id,
        orderData: payload,
        pipeline: props.pipeline,
      });
      useAlert(t(`${translationScope.value}.MODAL.SUCCESS_UPDATE`));
    }

    emit('success');
    dialogRef.value?.close();
  } catch (error) {
    useAlert(
      extractApiErrorMessage(
        error,
        t(`${translationScope.value}.MODAL.ERROR_MESSAGE`)
      )
    );
  } finally {
    isSubmitting.value = false;
  }
};

const onCancel = () => {
  dialogRef.value?.close();
};

watch(
  () => props.show,
  async isVisible => {
    if (!isVisible) {
      dialogRef.value?.close();
      return;
    }

    resetForm();

    if (props.mode === 'edit' && props.order?.id) {
      hydrateFormForEdit();
      if (form.contact_id) {
        await fetchContact(form.contact_id);
      }
    }

    await nextTick();
    dialogRef.value?.open();
  }
);
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="
      mode === 'create'
        ? $t(`${translationScope}.MODAL.NEW_TITLE`)
        : $t(`${translationScope}.MODAL.EDIT_TITLE`)
    "
    :description="$t(`${translationScope}.MODAL.SUBTITLE`)"
    :show-cancel-button="false"
    :show-confirm-button="false"
    width="2xl"
    overflow-y-auto
    @close="emit('close')"
  >
    <form
      class="flex max-h-[72vh] flex-col gap-4 overflow-y-auto pr-1"
      @submit.prevent="onSubmit"
    >
      <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Input
          v-model="form.title"
          :label="$t('CRM.MODAL.FORM.TITLE_LABEL')"
          :placeholder="$t('CRM.MODAL.FORM.TITLE_PLACEHOLDER')"
          :message="hasTitleError ? $t('CRM.MODAL.FORM.TITLE_REQUIRED') : ''"
          :message-type="hasTitleError ? 'error' : 'info'"
          @blur="v$.form.title.$touch"
        />

        <div class="min-w-0 flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('CRM.MODAL.FORM.COLUMN_LABEL') }}
          </label>
          <ComboBox
            :model-value="form.kanban_column_id"
            :options="columnOptions"
            :placeholder="selectedColumnLabel"
            :search-placeholder="$t('CRM.MODAL.FORM.COLUMN_SEARCH_PLACEHOLDER')"
            :empty-state="$t('CRM.MODAL.FORM.COLUMN_EMPTY')"
            :has-error="hasColumnError"
            :message="
              hasColumnError ? $t('CRM.MODAL.FORM.COLUMN_REQUIRED') : ''
            "
            @update:model-value="value => (form.kanban_column_id = value)"
          />
        </div>
      </div>

      <div class="rounded-xl border border-n-weak bg-n-alpha-1 p-3">
        <button
          type="button"
          class="flex w-full items-center justify-between text-left"
          @click="isOptionalSectionOpen = !isOptionalSectionOpen"
        >
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('CRM.MODAL.FORM.DETAILS_SECTION') }}
          </span>
          <span
            class="i-lucide-chevron-down size-4 text-n-slate-10 transition-transform duration-200"
            :class="{ 'rotate-180': isOptionalSectionOpen }"
          />
        </button>

        <div
          v-if="isOptionalSectionOpen || mode === 'edit'"
          class="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2"
        >
          <div class="min-w-0 flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-12">
              {{ $t('CRM.MODAL.FORM.CONTACT_LABEL') }}
            </label>
            <ComboBox
              use-api-results
              :model-value="form.contact_id"
              :options="contactOptions"
              :placeholder="selectedContactLabel"
              :search-placeholder="$t('CRM.MODAL.FORM.CONTACT_PLACEHOLDER')"
              :empty-state="
                isSearchingContacts
                  ? $t('CRM.MODAL.FORM.CONTACT_SEARCHING')
                  : $t('CRM.MODAL.FORM.CONTACT_EMPTY')
              "
              @update:model-value="onSelectContact"
              @search="onContactSearch"
            />
            <p
              v-if="
                contactsSearch.length > 0 &&
                contactsSearch.length < CONTACT_SEARCH_MIN_LENGTH &&
                !form.contact_id
              "
              class="text-xs text-n-slate-10"
            >
              {{ $t('CRM.MODAL.FORM.CONTACT_SEARCH_MIN') }}
            </p>
            <div
              v-if="selectedContact"
              class="flex items-center justify-between rounded-lg border border-n-weak bg-n-solid-2 px-2 py-1.5"
            >
              <div class="flex min-w-0 items-center gap-2">
                <Avatar
                  :src="selectedContactAvatar"
                  :name="selectedContact.name || ''"
                  :size="18"
                  rounded-full
                />
                <p class="truncate text-xs font-medium text-n-slate-11">
                  {{ selectedContactLabel }}
                </p>
              </div>
              <NextButton
                size="xs"
                variant="ghost"
                color="slate"
                icon="i-lucide-x"
                :title="$t('CRM.MODAL.FORM.CLEAR_CONTACT')"
                @click="clearSelectedContact"
              />
            </div>
          </div>

          <Input
            v-model="form.conversation_id"
            :label="$t('CRM.MODAL.FORM.CONVERSATION_LABEL')"
            :placeholder="$t('CRM.MODAL.FORM.CONVERSATION_PLACEHOLDER')"
            type="number"
          />

          <TextArea
            v-model="form.description"
            :label="$t('CRM.MODAL.FORM.DESCRIPTION_LABEL')"
            :placeholder="$t('CRM.MODAL.FORM.DESCRIPTION_PLACEHOLDER')"
            rows="3"
            class="md:col-span-2"
          />
        </div>
      </div>

      <div
        v-if="isSupportPipeline"
        class="rounded-xl border border-n-weak bg-n-alpha-1 p-3"
      >
        <h4 class="mb-3 text-sm font-medium text-n-slate-12">
          {{ $t('CRM.MODAL.FORM.SUPPORT_SIGNALS_SECTION') }}
        </h4>

        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div class="min-w-0 flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-12">
              {{ $t('CRM.MODAL.FORM.EMOTION_LABEL') }}
            </label>
            <select v-model="form.emotion_label" class="!mb-0 text-sm">
              <option
                v-for="option in emotionOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>

          <Input
            v-model="form.satisfaction_score"
            :label="$t('CRM.MODAL.FORM.SATISFACTION_LABEL')"
            type="number"
            min="1"
            max="5"
            step="1"
            :placeholder="$t('CRM.MODAL.FORM.SATISFACTION_PLACEHOLDER')"
          />

          <Input
            v-model="form.emotion_confidence"
            :label="$t('CRM.MODAL.FORM.CONFIDENCE_LABEL')"
            type="number"
            min="0"
            max="1"
            step="0.05"
            :placeholder="$t('CRM.MODAL.FORM.CONFIDENCE_PLACEHOLDER')"
          />

          <label
            class="inline-flex items-center gap-2 text-sm text-n-slate-12 sm:mt-6"
          >
            <input
              v-model="form.manual_override"
              type="checkbox"
              class="rounded border-n-weak"
            />
            {{ $t('CRM.MODAL.FORM.MANUAL_OVERRIDE_LABEL') }}
          </label>
        </div>

        <TextArea
          v-model="form.emotion_reason"
          :label="$t('CRM.MODAL.FORM.EMOTION_REASON_LABEL')"
          :placeholder="$t('CRM.MODAL.FORM.EMOTION_REASON_PLACEHOLDER')"
          rows="2"
          class="mt-3"
        />
      </div>

      <div v-else class="rounded-xl border border-n-weak bg-n-alpha-1 p-3">
        <h4 class="mb-3 text-sm font-medium text-n-slate-12">
          {{ $t('CRM.MODAL.FORM.SERVICE_DETAILS_SECTION') }}
        </h4>

        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          <Input
            v-model="form.value"
            :label="$t('CRM.MODAL.FORM.VALUE_LABEL', { currency })"
            type="number"
            min="0"
            step="0.01"
            :placeholder="$t('CRM.MODAL.FORM.VALUE_PLACEHOLDER')"
          />

          <Input
            v-model="form.difficulty_level"
            :label="$t('CRM.MODAL.FORM.DIFFICULTY_LABEL')"
            type="number"
            min="1"
            max="5"
            step="1"
            :placeholder="$t('CRM.MODAL.FORM.DIFFICULTY_PLACEHOLDER')"
          />

          <div class="min-w-0 flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-12">
              {{ $t('CRM.MODAL.FORM.PRIORITY_LABEL') }}
            </label>
            <select v-model="form.priority_level" class="!mb-0 text-sm">
              <option value="">
                {{ $t('CRM.MODAL.FORM.PRIORITY_PLACEHOLDER') }}
              </option>
              <option
                v-for="option in priorityOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>

          <Input
            v-model="form.due_date"
            :label="$t('CRM.MODAL.FORM.DUE_DATE_LABEL')"
            type="date"
          />

          <Input
            v-model="form.estimated_hours"
            :label="$t('CRM.MODAL.FORM.ESTIMATED_HOURS_LABEL')"
            type="number"
            min="0"
            step="0.5"
            :placeholder="$t('CRM.MODAL.FORM.ESTIMATED_HOURS_PLACEHOLDER')"
          />

          <Input
            v-model="form.actual_hours"
            :label="$t('CRM.MODAL.FORM.ACTUAL_HOURS_LABEL')"
            type="number"
            min="0"
            step="0.5"
            :placeholder="$t('CRM.MODAL.FORM.ACTUAL_HOURS_PLACEHOLDER')"
          />
        </div>
      </div>

      <div
        class="sticky bottom-0 z-10 -mx-1 mt-2 flex flex-wrap justify-end gap-2 border-t border-n-weak bg-n-alpha-3 px-1 pb-1 pt-3 backdrop-blur-sm"
      >
        <NextButton
          color="slate"
          variant="ghost"
          :label="$t('CRM.MODAL.FORM.CANCEL')"
          :disabled="isSubmitting"
          @click="onCancel"
        />

        <NextButton
          color="blue"
          :label="
            mode === 'create'
              ? $t(`${translationScope}.MODAL.CREATE_SUBMIT`)
              : $t(`${translationScope}.MODAL.UPDATE_SUBMIT`)
          "
          :is-loading="isSubmitting"
          type="submit"
        />
      </div>
    </form>
  </Dialog>
</template>
